<template>
  <div ref="buehne" class="tv-buehne" :class="`tv-buehne-${geraet}`">
    <div class="tv-seite" :data-test="`${testPrefix}-seite`" :style="seitenStil">
      <slot :geraet="geraet" />
    </div>
    <div class="tv-geraete" role="group">
      <button
        v-for="g in ['desktop', 'tablet', 'handy']"
        :key="g"
        type="button"
        class="tv-geraet"
        :class="{ aktiv: geraet === g }"
        :aria-pressed="String(geraet === g)"
        :data-test="`${testPrefix}-geraet-${g}`"
        @click="$emit('update:geraet', g)"
      >{{ $t(`settings.project.corporateIdentity.typo.geraet.${g}`) }}</button>
    </div>
  </div>
</template>

<script>
// The stage of a CI preview (spec 2026-09-30 brand kit, Rev. 2 #6): a page in
// the slot, zoomed down to the stage width (never up), a device switch below.
// Shared by TypoVorschau and CiBrandVorschau; the parent owns `geraet` (.sync).
export default {
  name: 'TypoBuehne',
  props: {
    geraet: { type: String, default: 'desktop' },
    // Page width per device in px; desktop and tablet are zoomed to the stage.
    breiten: { type: Object, default: () => ({ desktop: 720, tablet: 600, handy: 390 }) },
    // A preview, not the page itself: never larger than this.
    maxZoom: { type: Number, default: 0.6 },
    testPrefix: { type: String, default: 'typo' },
  },
  data: () => ({
    // Inner width of the stage; the desktop page is zoomed down to it.
    buehnenBreite: 720,
  }),
  computed: {
    seitenStil() {
      if (this.geraet === 'handy') return { width: `${this.breiten.handy}px`, '--tv-zoom': 1 };
      const breite = this.breiten[this.geraet] || this.breiten.desktop;
      const zoom = Math.min(this.maxZoom, this.buehnenBreite / breite);
      return { width: `${breite}px`, zoom, '--tv-zoom': zoom };
    },
  },
  mounted() {
    this.messen();
    // Guard: nothing to observe without a stage (Rev. 2 #5).
    if (!this.$refs.buehne) return;
    if (typeof ResizeObserver === 'function') {
      this.beobachter = new ResizeObserver(() => this.messen());
      this.beobachter.observe(this.$refs.buehne);
    } else {
      window.addEventListener('resize', this.messen);
    }
  },
  beforeDestroy() {
    if (this.beobachter) this.beobachter.disconnect();
    window.removeEventListener('resize', this.messen);
  },
  methods: {
    messen() {
      const el = this.$refs.buehne;
      if (!el) return;
      const cs = window.getComputedStyle(el);
      const breite = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      if (breite > 0) this.buehnenBreite = breite;
    },
  },
};
</script>

<style scoped>
.tv-buehne { box-sizing: border-box; background: #36393d; border-radius: 12px; padding: 16px; overflow: hidden; }
.tv-buehne-handy { overflow-x: auto; }
.tv-seite {
  flex-shrink: 0; box-sizing: border-box; background: #fff; border-radius: 8px;
  color: #1f2226; margin: 0 auto;
}
.tv-geraete { width: fit-content; margin: 12px auto 0; display: flex; gap: 4px; padding: 4px; background: #36393d; border-radius: 8px; }
.tv-geraet { all: unset; cursor: pointer; padding: 6px 14px; font-size: 14px; border-radius: 6px; color: #d9dadc; }
.tv-geraet.aktiv { background: #5b6064; color: #fff; font-weight: 700; }
.tv-geraet:focus-visible { outline: 2px solid #51d182; }
</style>
